// ─────────────────────────────────────────────────────────────────────
// <VerticalLandingPage /> — /v/:slug
//
// Vertical-specific landing: hero with serif glyph + chip filters + grid.
// Verticals: live-events | sports | theatre | attractions | workshops | spiritual
// ─────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import api from '../api/client';
import { useLocation as useCity } from '../context/LocationContext';
import EventCard from '../components/event/EventCard';
import ChipFilterRow from '../components/common/ChipFilterRow';

const VERTICAL_META = {
  'live-events': {
    title: 'Live Events',
    titleAccent: 'tonight.',
    glyph: '✦',
    variant: 'gold',
    eyebrow: 'Concerts · Comedy · Indie',
    lede: 'Music, comedy, theatre, festivals — anything that happens once and is gone.',
  },
  'sports': {
    title: 'Sports',
    titleAccent: 'live.',
    glyph: '◯',
    variant: 'sage',
    eyebrow: 'Cricket · Football · Pickleball',
    lede: 'Spectator events and court bookings. Pick a date, pick a slot.',
  },
  'theatre': {
    title: 'Plays',
    titleAccent: 'and theatre.',
    glyph: 'थ',
    variant: 'rose',
    eyebrow: 'Theatre · Drama · Storytelling',
    lede: 'Plays, multi-show runs, intimate venues. Curated for theatre-lovers.',
  },
  'attractions': {
    title: 'Attractions',
    titleAccent: 'and activities.',
    glyph: '⌖',
    variant: 'slate',
    eyebrow: 'Museums · Theme parks · Slot bookings',
    lede: 'Time-slot entry to museums, theme parks, science centres and family attractions.',
  },
  'workshops': {
    title: 'Workshops',
    titleAccent: 'and learning.',
    glyph: '✎',
    variant: 'plum',
    eyebrow: 'Pottery · Photography · Cooking',
    lede: 'Hands-on classes by makers, artists, and teachers in your city.',
  },
  'spiritual': {
    title: 'Spiritual',
    titleAccent: 'gatherings.',
    glyph: 'ॐ',
    variant: 'gold',
    eyebrow: 'Bhakti · Classical · Devotional',
    lede: 'Devotional concerts, classical evenings, satsangs and quiet rooms.',
  },
};

const QUICK_CHIPS = [
  { value: 'all',          label: 'All' },
  { value: 'today',        label: 'Today' },
  { value: 'this-weekend', label: 'This weekend' },
  { value: 'under-500',    label: 'Under ₹500' },
  { value: 'free',         label: 'Free' },
  { value: 'family',       label: 'Family-friendly' },
];

export default function VerticalLandingPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { city } = useCity();
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const meta = VERTICAL_META[slug] || VERTICAL_META['live-events'];
  const chip = params.get('when') || params.get('cat') || 'all';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // r7.6.4: `?cat=` is a taxonomy slug (family → family-kids); the list answers with `rows`
        const cat = params.get('cat');
        const CAT_ALIAS = { family: 'family-kids', concert: 'music', comedy: 'comedy-theatre', classical: 'music', devotional: 'cultural', indie: 'music' };
        const { data } = await api.get('/events', {
          params: {
            vertical: slug,
            city,
            when: params.get('when') || undefined,
            category: cat ? (CAT_ALIAS[cat] || cat) : undefined,
            limit: 24,
          },
        });
        if (!cancelled) setEvents(data.rows || data.events || []);
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, city, params]);

  const setChip = (value) => {
    const next = new URLSearchParams(params);
    if (value === 'all') {
      next.delete('when'); next.delete('cat');
    } else if (value === 'family') {
      next.set('cat', 'family');
    } else {
      next.set('when', value); next.delete('cat');
    }
    setParams(next, { replace: true });
  };

  return (
    <div>
      {/* Hero */}
      <section className={`snt-art-${meta.variant} relative overflow-hidden`}>
        <div className="snt-container py-20 lg:py-28 grid lg:grid-cols-[1.4fr_1fr] gap-12 items-center relative">
          <div>
            <div className="snt-eyebrow mb-5">{meta.eyebrow}</div>
            <h1
              className="font-serif font-light mb-5"
              style={{
                fontSize: 'clamp(48px, 8vw, 96px)',
                lineHeight: 0.95,
                letterSpacing: '-0.035em',
                color: 'var(--snt-text)',
              }}
            >
              {meta.title}{' '}
              <span className="snt-em-accent">{meta.titleAccent}</span>
            </h1>
            <p className="text-[16px] leading-relaxed max-w-[480px]" style={{ color: 'var(--snt-text-2)' }}>
              {meta.lede}
            </p>
          </div>
          <div
            className="aspect-[4/5] rounded-card flex items-center justify-center font-serif overflow-hidden"
            style={{
              fontSize: 'clamp(120px, 22vw, 280px)',
              opacity: 0.25,
              color: `var(--snt-category-${variantIdx(meta.variant)})`,
            }}
          >
            {meta.glyph}
          </div>
        </div>
      </section>

      {/* Filter chips */}
      <ChipFilterRow chips={QUICK_CHIPS} active={chip} onChange={setChip} />

      {/* Results */}
      <section className="snt-container py-10 lg:py-14">
        {loading ? (
          <div className="text-[13px]" style={{ color: 'var(--snt-text-3)' }}>
            {t('common.loading', 'Loading…')}
          </div>
        ) : events.length === 0 ? (
          <div className="py-16 text-center">
            <h3 className="font-serif text-[26px] mb-2" style={{ color: 'var(--snt-text)' }}>
              {t('vertical.empty_title', 'Nothing here yet')}
            </h3>
            <p className="text-[13px]" style={{ color: 'var(--snt-text-3)' }}>
              {t('vertical.empty_hint', 'Try a different filter or come back tomorrow.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
            {events.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function variantIdx(v) {
  return { gold: 1, slate: 2, rose: 3, sage: 4, plum: 5 }[v] || 1;
}
